import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  FormControlLabel,
  Switch,
  Grid,
  FormHelperText,
  Box
} from '@mui/material';

const UserDialog = ({ open, user, onClose, onSubmit, currentUser }) => {
  const [formData, setFormData] = useState({
    username: '',
    email: '',
    password: '',
    is_admin: false,
    is_active: true
  });
  
  const [errors, setErrors] = useState({});
  const [isEditMode, setIsEditMode] = useState(false);
  
  // When the dialog opens, initialize form data
  useEffect(() => {
    if (user) {
      setFormData({
        username: user.username || '',
        email: user.email || '',
        password: '', // Don't prefill password for security
        is_admin: user.is_admin || false,
        is_active: user.is_active || false
      });
      setIsEditMode(true);
    } else {
      setFormData({
        username: '',
        email: '',
        password: '',
        is_admin: false,
        is_active: true
      });
      setIsEditMode(false);
    }
    setErrors({});
  }, [user]);
  
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value
    });
    
    // Clear error when field is edited
    if (errors[name]) {
      setErrors({
        ...errors,
        [name]: null
      });
    }
  };
  
  const handleSwitchChange = (e) => {
    const { name, checked } = e.target;
    
    // Prevent changing own admin status
    if (isEditMode && name === 'is_admin' && user.id === currentUser?.id && !checked) {
      setErrors({
        ...errors,
        is_admin: 'You cannot remove admin rights from your own account'
      });
      return;
    }
    
    setFormData({
      ...formData,
      [name]: checked
    });
  };
  
  const validate = () => {
    const newErrors = {};
    
    if (!formData.username.trim()) {
      newErrors.username = 'Username is required';
    }
    
    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = 'Email is invalid';
    }
    
    if (!isEditMode && !formData.password.trim()) {
      newErrors.password = 'Password is required for new users';
    } else if (!isEditMode && formData.password.length < 6) {
      newErrors.password = 'Password must be at least 6 characters';
    } else if (isEditMode && formData.password.trim() && formData.password.length < 6) {
      newErrors.password = 'Password must be at least 6 characters';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };
  
  const handleSubmit = (e) => {
    e.preventDefault();
    
    if (!validate()) {
      return;
    }
    
    // Only include password if it's provided (for updates)
    const dataToSubmit = { ...formData };
    if (isEditMode && !dataToSubmit.password) {
      delete dataToSubmit.password;
    }
    
    onSubmit(dataToSubmit);
  };
  
  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{isEditMode ? 'Edit User' : 'Create New User'}</DialogTitle>
      <form onSubmit={handleSubmit}>
        <DialogContent>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <TextField
                name="username"
                label="Username"
                value={formData.username}
                onChange={handleChange}
                fullWidth
                required
                error={!!errors.username}
                helperText={errors.username}
                disabled={isEditMode} // Username can't be changed once created
              />
            </Grid>
            
            <Grid item xs={12}>
              <TextField
                name="email"
                label="Email"
                type="email"
                value={formData.email}
                onChange={handleChange}
                fullWidth
                required
                error={!!errors.email}
                helperText={errors.email}
              />
            </Grid>
            
            <Grid item xs={12}>
              <TextField
                name="password"
                label={isEditMode ? "New Password (leave blank to keep current)" : "Password"}
                type="password"
                value={formData.password}
                onChange={handleChange}
                fullWidth
                required={!isEditMode}
                error={!!errors.password}
                helperText={errors.password}
              />
            </Grid>
            
            <Grid item xs={12} sm={6}>
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.is_admin}
                    onChange={handleSwitchChange}
                    name="is_admin"
                    color="primary"
                    disabled={isEditMode && user.id === currentUser?.id} // Can't change own admin status
                  />
                }
                label="Administrator"
              />
              {errors.is_admin && (
                <FormHelperText error>{errors.is_admin}</FormHelperText>
              )}
              <Box mt={1}>
                <FormHelperText>
                  Administrators can manage users, clinics, and practitioners
                </FormHelperText>
              </Box>
            </Grid>
            
            <Grid item xs={12} sm={6}>
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.is_active}
                    onChange={handleSwitchChange}
                    name="is_active"
                    color="primary"
                    disabled={isEditMode && user.id === currentUser?.id} // Can't deactivate own account
                  />
                }
                label="Active"
              />
              <Box mt={1}>
                <FormHelperText>
                  Inactive users cannot log in to the system
                </FormHelperText>
              </Box>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose} color="secondary">
            Cancel
          </Button>
          <Button type="submit" color="primary" variant="contained">
            {isEditMode ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};

export default UserDialog;